import { css } from '@emotion/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  AnimatePresence, motion, useAnimate,
} from 'motion/react';
import {
  type FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import {
  type AdditionalToolboxAction, ToolboxContainerComponent,
} from '~/_shared/components/toolboxContainer/toolboxContainer.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useTranslation } from '~/_shared/utils/hooks';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import { type SidebarAppProps } from '../sidebarAppComponentProps.type';
import {
  mapToolComponentMap, mapToolIconMap, mapToolTitleMap,
} from './mapTools/mapTools.helpers';
import { ToolSelectorComponent } from './mapTools/toolSelector.component';

const toolsSelectorWrapperStyle = css({
  overflow: 'hidden',
  position: 'absolute',
  zIndex: 50,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  willChange: 'transform, opacity',
});

const toolsSelectorContentStyle = (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.primary,
});

const toolsSelectorBackgroundStyle = (theme: Theme) => css({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 10,
  width: '100%',
  height: '100%',
  background: theme.backgroundColors.primary,
  willChange: 'opacity',
  pointerEvents: 'none',
});

const toolSelectorBackgroundInitialStyle = {
  opacity: 1,
};
const toolSelectorBackgroundFinishedStyle = {
  opacity: 0,
};

const toolsSelectorInitialStyles = {
  transform: 'translateY(0)',
};

const toolsSelectorFinishedStyles = {
  transform: 'translateY(-100%)',
};

type MapToolsComponentProps = SidebarAppProps & Readonly<{
  selectedTool: MapTool | null;
  isBoundaryToolDisabled: boolean;
  isEditModeDisabled: boolean;
  isToolSelectEnabled: boolean;
  additionalToolboxAction?: AdditionalToolboxAction;

  closeTool: () => void;
  openTool: (tool: MapTool) => void;
}>;

export const MapToolsComponent: FC<MapToolsComponentProps> = props => {
  const [isSelectorRendered, setIsSelectorRendered] = useState<boolean>(!props.selectedTool);
  const [toolSelectorScope, toolSelectorAnimate] = useAnimate();
  const [backgroundScope, backgroundAnimate] = useAnimate();
  const [lastSelectedTool, setLastSelectedTool] = useState(props.selectedTool);
  const [displayedTool, setDisplayedTool] = useState(props.selectedTool);

  const previousTool = usePrevious(props.selectedTool);
  const showSelector = !props.selectedTool;
  const previousShowSelector = usePrevious(showSelector);

  const { openTool, closeTool } = props;
  const [t] = useTranslation();

  const SelectedComponent = useMemo(() =>
    displayedTool && mapToolComponentMap[displayedTool],
  [displayedTool]);

  const titleIcon = useMemo((): IconProp | undefined => {
    if (displayedTool && !showSelector) {
      return mapToolIconMap[displayedTool];
    }
    return undefined;
  }, [showSelector, displayedTool]);

  const toggleSelector = useCallback(() => {
    if (!props.isToolSelectEnabled) {
      return;
    }
    else if (!showSelector) {
      closeTool();
    }
    else {
      openTool(lastSelectedTool ?? MapTool.Grouping);
    }
  },
  [showSelector, lastSelectedTool, props.isToolSelectEnabled, closeTool, openTool]);

  useEffect(() => {
    if (previousShowSelector === showSelector) {
      return;
    }

    if (showSelector) {
      setIsSelectorRendered(true);
      backgroundAnimate(backgroundScope.current, toolSelectorBackgroundInitialStyle);
      toolSelectorAnimate(toolSelectorScope.current, toolsSelectorInitialStyles)
        .then(() => {
          setDisplayedTool(null);
        });
    }
    else {
      setDisplayedTool(props.selectedTool);
      backgroundAnimate(backgroundScope.current, toolSelectorBackgroundFinishedStyle);
      toolSelectorAnimate(toolSelectorScope.current, toolsSelectorFinishedStyles)
        .then(() => {
          setIsSelectorRendered(false);
        });
    }
  }, [showSelector, previousShowSelector, props.selectedTool, backgroundAnimate, backgroundScope, toolSelectorAnimate, toolSelectorScope]);

  useEffect(() => {
    if (props.selectedTool) {
      setLastSelectedTool(props.selectedTool);
    }

    if (previousTool && props.selectedTool) {
      if (previousTool !== props.selectedTool) {
        setDisplayedTool(props.selectedTool);
      }
    }
  }, [previousTool, props.selectedTool]);

  return (
    <ToolboxContainerComponent
      title={displayedTool && !showSelector ? t(mapToolTitleMap[displayedTool]) : t('Select tool')}
      titleIcon={titleIcon}
      additionalAction={props.additionalToolboxAction}
      onClose={props.onClose}
      onTitleClick={toggleSelector}
    >
      <AnimatePresence>
        <motion.div
          key="map-tools-tools-selector-background"
          ref={backgroundScope}
          css={toolsSelectorBackgroundStyle}
          initial={toolSelectorBackgroundInitialStyle}
          exit={toolSelectorBackgroundFinishedStyle}
          transition={{ duration: .4 }}
        />
        <motion.div
          key="tools-selector"
          ref={toolSelectorScope}
          css={toolsSelectorWrapperStyle}
          initial={toolsSelectorInitialStyles}
          exit={toolsSelectorFinishedStyles}
          transition={{ duration: .3 }}
        >
          {isSelectorRendered && (
            <ScrollBarComponent type={ScrollbarType.Vertical}>
              <ToolSelectorComponent
                css={toolsSelectorContentStyle}
                isBoundaryToolDisabled={props.isBoundaryToolDisabled}
                isEditModeDisabled={props.isEditModeDisabled}
                selectTool={props.openTool}
              />
            </ScrollBarComponent>
          )}
        </motion.div>
      </AnimatePresence>

      {displayedTool && SelectedComponent && <SelectedComponent />}
    </ToolboxContainerComponent>
  );
};
